// WO-P3 §PARCEL PROFILE — LEFT identity dossier.
//
// Reads the same canonical ViewerContextState the RIGHT ContextPanel
// consumes; renders identity/ownership/coordinates as an aerospace-
// chamfer dossier that visually belongs to the same world as WO-P1's
// shell (INSTRUMENT/CONTEXT/section-index-tag).
//
// Authority: strictly presentation. No fabrication:
//   - Parcel ID / Owner / Position / Size come from ViewerParcelDetail
//     (indexed parcel record via GridViewer).
//   - Zone comes from the canonical ZoneClassificationStatus fetched
//     shell-side (WO#3c) — never guessed from geometry.
//   - Claim block / Claim tx are indexed record fields, rendered only
//     when actually present.
//   - Snapshot/reconciled/errors are surfaced by the RIGHT panel
//     (actions/state), NOT here — this split enforces Neo's
//     LEFT=identity / RIGHT=actions directive without duplication.
//
// Empty state ("SELECT TERRITORY"): reticle + scan marks + copy in the
// reference language. Never a placeholder for future fields
// (title/website/social/media/blueprint/etc.) — those sections stay
// hidden until backend truth exists.

import type { ViewerContextState } from "../viewer/context-state";
import type { ZoneClassificationStatus } from "./useZoneClassification";
import { TerritoryCapacity } from "./TerritoryCapacity";
import { ParcelArtworks } from "./ParcelArtworks";
import { useTerritoryName } from "./useTerritoryName";
import { OwnerModerationBanner } from "./moderation/OwnerModerationBanner";
import { useOwnedTerritoryIdentityId } from "./moderation/useOwnedTerritoryIdentityId";
import { SellLandGridEntry } from "./marketplace/SellLandGridEntry";
import { useI18n } from "../i18n";
import { cn } from "./cn";

const EXPLORER_TX = (tx: string) => `https://sepolia.basescan.org/tx/${tx}`;
const EXPLORER_BLOCK = (n: number | string) =>
  `https://sepolia.basescan.org/block/${n}`;

export interface ParcelProfilePanelProps {
  context: ViewerContextState;
  zoneInfo?: ZoneClassificationStatus;
  testId?: string;
  /** WO-VRD-30 — render only the inner content (no aside/header) so it
   * can be embedded inside a SideDrawer that owns the shell + title. */
  bare?: boolean;
}

export function ParcelProfilePanel({
  context,
  zoneInfo,
  testId,
  bare = false,
}: ParcelProfilePanelProps) {
  const { t } = useI18n();
  if (bare) {
    return (
      <div
        data-testid={testId ?? "parcel-profile"}
        data-shell-variant="wo-p3"
        className="px-4 py-4 select-text"
      >
        {context.kind === "parcel" && (
          <PopulatedIdentity detail={context.detail} zoneInfo={zoneInfo} />
        )}
        {context.kind !== "parcel" && <EmptyIdentity kind={context.kind} />}
      </div>
    );
  }
  return (
    <aside
      data-testid={testId ?? "parcel-profile"}
      data-shell-variant="wo-p3"
      aria-label={t("grid.panel.parcelProfileAria")}
      className={cn(
        "panel-chamfer",
        // WO-VRD-12 (Claudio) — mirror the Context panel's box exactly:
        // same width, same min/max height, scrollable body.
        "w-[340px] lg:w-[344px] max-w-[calc(100vw-32px)]",
        "max-h-[calc(50%-46px)] min-h-[160px]",
        "flex flex-col overflow-visible",
        "select-text",
      )}
    >
      <span className="corner-ticks" aria-hidden>
        <span className="ct-a" />
        <span className="ct-b" />
      </span>
      <header
        data-testid="parcel-profile-header"
        className="chamfer-strip"
      >
        <span className="strip-dot" aria-hidden />
        <span>{t("grid.panel.parcelProfileTitle")}</span>
      </header>
      <div className="px-4 py-4 flex-1 overflow-y-auto scroll-dark">
        {context.kind === "parcel" && (
          <PopulatedIdentity
            detail={context.detail}
            zoneInfo={zoneInfo}
          />
        )}
        {context.kind !== "parcel" && (
          <EmptyIdentity kind={context.kind} />
        )}
      </div>
    </aside>
  );
}

function PopulatedIdentity({
  detail,
  zoneInfo,
}: {
  detail: import("../viewer/context-state").ViewerParcelDetail;
  zoneInfo?: ZoneClassificationStatus;
}) {
  const { t } = useI18n();
  const p = detail.parcel;
  const zoneLabel =
    zoneInfo?.status === "available" ? zoneInfo.zone : null;
  const territoryName = useTerritoryName({ id: p.id, owner: p.owner });
  const modIdentityId = useOwnedTerritoryIdentityId({ id: p.id, owner: p.owner });
  return (
    <div
      data-testid="parcel-profile-populated"
      className="flex flex-col gap-3"
    >
      {modIdentityId && (
        <OwnerModerationBanner targetType="territory" targetId={modIdentityId} />
      )}
      {/* Identity header: PARCEL #ID · larger display + spec cyan */}
      <div className="flex items-baseline gap-3">
        <span
          className="font-mono text-[9px] uppercase tracking-[0.32em] text-text-muted"
        >
          {t("grid.panel.parcel")}
        </span>
        <span
          data-testid="pp-parcel-id"
          className="font-display text-[24px] font-semibold tracking-tight tabular-nums"
          style={{ color: "var(--spec-cyan)", textShadow: "0 0 12px var(--spec-cyan-glow)" }}
        >
          #{p.id}
        </span>
      </div>

      {/* Territory name (owner-set identity). Rendered only when a named
          identity is authoritatively anchored to this parcel — never faked. */}
      {territoryName.status === "ready" && (
        <Section title={t("grid.panel.territoryName")}>
          <span
            data-testid="pp-territory-name"
            className="font-display text-[15px] font-semibold tracking-tight break-words"
            style={{ color: "#ff2d55", textShadow: "0 0 10px rgba(255,45,85,0.75)" }}
          >
            {territoryName.name}
          </span>
        </Section>
      )}

      {/* Divider hairline */}
      <div
        aria-hidden
        className="h-px w-full"
        style={{ background: "var(--spec-hairline)" }}
      />

      {/* Ownership */}
      <Section title={t("grid.panel.owner")}>
        <span
          data-testid="pp-owner"
          className="break-all font-mono text-[11px] leading-relaxed text-text-secondary"
          title={p.owner}
        >
          {p.owner}
        </span>
      </Section>

      {/* Entry B — REAL Sell Land affordance for the authenticated OWNER of this
          parcel. Opens the SAME shared seller wizard as MY MARKET → LIST LAND.
          Gated by real ownership + marketplace readiness, NOT the demo flag. */}
      <SellLandGridEntry
        parcel={{ parcel_id: Number(p.id), x: p.x, y: p.y, w: p.w, h: p.h, owner: p.owner }}
      />

      {/* Coordinates + Size */}
      <div className="grid grid-cols-2 gap-3">
        <Section title={t("grid.panel.position")}>
          <span
            data-testid="pp-position"
            className="font-mono text-[12px] text-text-primary tabular-nums"
          >
            {p.x}, {p.y}
          </span>
        </Section>
        <Section title={t("grid.panel.size")}>
          <span
            data-testid="pp-size"
            className="font-mono text-[12px] text-text-primary tabular-nums"
          >
            {p.w} × {p.h}
          </span>
        </Section>
      </div>

      <Section title={t("grid.panel.slots")}>
        <span
          data-testid="pp-slots"
          className="font-mono text-[12px] text-text-primary tabular-nums"
        >
          {p.w * p.h}
        </span>
      </Section>

      {/* Zone — rendered only when classification is available/ok. */}
      {zoneLabel && (
        <Section title={t("grid.panel.zone")}>
          <span
            data-testid="pp-zone"
            className="font-mono text-[11px] uppercase tracking-[0.18em]"
            style={{ color: "var(--spec-cyan)" }}
          >
            {zoneLabel}
          </span>
        </Section>
      )}

      {/* Claim block */}
      {p.claim_block != null && (
        <Section title={t("grid.panel.claimBlock")}>
          <a
            data-testid="pp-claim-block"
            href={EXPLORER_BLOCK(p.claim_block)}
            target="_blank"
            rel="noreferrer noopener"
            className="font-mono text-[12px] tabular-nums text-text-secondary hover:text-[color:var(--spec-cyan)] transition-colors"
          >
            {p.claim_block}
          </a>
        </Section>
      )}

      {/* Claim tx (explorer link) */}
      {p.claim_tx && (
        <Section title={t("grid.panel.claimTx")}>
          <a
            data-testid="pp-claim-tx"
            href={EXPLORER_TX(p.claim_tx)}
            target="_blank"
            rel="noreferrer noopener"
            className="break-all font-mono text-[11px] leading-relaxed text-text-secondary hover:text-[color:var(--spec-cyan)] transition-colors"
            title={p.claim_tx}
          >
            {truncMid(p.claim_tx, 10, 8)} ↗
          </a>
        </Section>
      )}

      {/* WO#2 — READ-ONLY territory capacity (area / publishability / GP / units). */}
      <TerritoryCapacity parcel={{ id: p.id, owner: p.owner }} />

      {/* Divider hairline */}
      <div aria-hidden className="h-px w-full" style={{ background: "var(--spec-hairline)" }} />

      {/* #15 Phase C — ARTWORKS ON THIS LAND (authoritative placement geometry). */}
      <ParcelArtworks parcel={{ id: p.id, x: p.x, y: p.y, w: p.w, h: p.h }} />
    </div>
  );
}

function EmptyIdentity({
  kind,
}: {
  kind: "empty" | "selection";
}) {
  const { t } = useI18n();
  // Neutral truth: neither loaded nor missing. We deliberately do NOT
  // pre-echo the future dossier sections (title/website/socials/etc.);
  // showing hidden "NOT CONFIGURED" chips for content that has no
  // backend surface would be a form of fake data.
  return (
    <div
      data-testid="parcel-profile-empty"
      className="flex flex-col items-center justify-center gap-4 py-6 text-center"
    >
      {/* Reticle — precision targeting mark */}
      <div
        aria-hidden
        className="relative h-16 w-16"
        style={{ borderColor: "var(--spec-hairline)" }}
      >
        <span
          className="absolute -top-px -left-px h-2 w-2 border-t border-l"
          style={{ borderColor: "var(--spec-cyan)" }}
        />
        <span
          className="absolute -top-px -right-px h-2 w-2 border-t border-r"
          style={{ borderColor: "var(--spec-cyan)" }}
        />
        <span
          className="absolute -bottom-px -left-px h-2 w-2 border-b border-l"
          style={{ borderColor: "var(--spec-cyan)" }}
        />
        <span
          className="absolute -bottom-px -right-px h-2 w-2 border-b border-r"
          style={{ borderColor: "var(--spec-cyan)" }}
        />
        <span className="absolute top-1/2 left-1/2 h-2 w-px -translate-x-1/2 -translate-y-1/2 bg-text-muted" />
        <span className="absolute top-1/2 left-1/2 h-px w-2 -translate-x-1/2 -translate-y-1/2 bg-text-muted" />
      </div>
      <div className="space-y-1.5">
        <div
          className="font-mono text-[10px] tracking-[0.32em] uppercase"
          style={{ color: "var(--spec-cyan)" }}
        >
          {t("grid.panel.selectTerritory")}
        </div>
        <div className="text-[11px] text-text-muted leading-relaxed max-w-[220px]">
          {kind === "selection"
            ? t("grid.panel.emptySelection")
            : t("grid.panel.emptyDefault")}
        </div>
      </div>
    </div>
  );
}

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1">
      <span className="font-mono text-[8.5px] uppercase tracking-[0.32em] text-text-muted">
        {title}
      </span>
      {children}
    </div>
  );
}

function truncMid(s: string, keepLeft: number, keepRight: number): string {
  if (s.length <= keepLeft + keepRight + 1) return s;
  return `${s.slice(0, keepLeft)}…${s.slice(-keepRight)}`;
}
